import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS, MomentDateAdapter } from '@angular/material-moment-adapter';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MatDatepicker } from '@angular/material/datepicker';
import { TranslateService } from '@ngx-translate/core';
import moment, { Moment } from 'moment';
import { Subscription } from 'rxjs';
import { LanguageService } from 'src/shared/services/language.service';

export const MY_DATE_FORMATS = {
  parse: {
    dateInput: 'DD/MM/YYYY',
  },
  display: {
    dateInput: 'DD/MM/YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

@Component({
  selector: 'atom-date',
  templateUrl: './date.component.html',
  styleUrls: ['./date.component.scss'],
  providers: [
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
    },
    { provide: MAT_DATE_FORMATS, useValue: MY_DATE_FORMATS },
  ],
})
export class DateComponent implements OnInit, OnDestroy {
  @Input() title: string = '';
  @Input() disableFutureDates: boolean = true;
  @Input() set defaultValue(val: Date | null) {
    if (!val) return;
    if (this.dateValue) {
      this.dateValue.patchValue(val);
    } else {
      this._initialValue = val;
    }
  }

  @Output() dateSelected = new EventEmitter<Moment | null>();
  @ViewChild('picker') picker!: MatDatepicker<any>;
  public translatedTitle: string = '';
  public maxDate: Date = new Date();
  public dateValue!: FormControl;
  private langSubscription: Subscription;
  private translateSubscription?: Subscription;
  private _initialValue: Date | null = null;

  constructor(private dateAdapter: DateAdapter<any>, private languageService: LanguageService,
    private translateService: TranslateService
  ) {
    this.langSubscription = this.languageService.currentLanguage$.subscribe(lang => {
      this.dateAdapter.setLocale(lang);
      moment.locale(lang);
      this.updateTitleTranslation();
    });

    const currentLang = this.languageService.getCurrentLanguage();
    this.dateAdapter.setLocale(currentLang);
    moment.locale(currentLang);
  }

  ngOnInit(): void {
    // Aplicar validadores condicionalmente basado en disableFutureDates
    const validators = this.disableFutureDates ? [Validators.max(this.maxDate.getTime())] : [];
    this.dateValue = new FormControl(null, validators);

    // Si antes recibió un valor, aplicarlo ahora
    if (this._initialValue) {
      this.dateValue.patchValue(this._initialValue);
      this._initialValue = null;
    }

    this.dateValue.valueChanges.subscribe((value) => {
      // Emitir el evento original
      this.dateSelected.emit(value);
    });

    this.updateTitleTranslation();
    this.translateSubscription = this.translateService.onLangChange.subscribe(() => {
      this.updateTitleTranslation();
    });
  }

  openDatePicker(): void {
    this.picker.open();
  }

  private updateTitleTranslation() {
    if (this.title && this.title.trim() !== '') {
      this.translateService.get(this.title).subscribe(translation => {
        this.translatedTitle = translation;
      });
    }
  }

  // Método para resetear la fecha
  reset(): void {
    this.dateValue.reset();
    this.dateSelected.emit(null);
  }

  ngOnDestroy() {
    if (this.langSubscription) {
      this.langSubscription.unsubscribe();
    }
    if (this.translateSubscription) {
      this.translateSubscription.unsubscribe();
    }
  }
}
